import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Globe, ImagePlus, Loader2, Trash2, X } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import {
  LIMITS, PHOTO_ACCEPT, adminPhotoSrc, consentLine, isPublic, left, photoProblem, profileOf,
} from "../../../lib/trainerProfile";
import SpeakerAvatar from "../../SpeakerAvatar";

/**
 * "Profil pe site" (16.10.2026): what a cohort's card shows of its trainer —
 * the name, a one-line title, a short description and a photo. The team
 * fills it in; nothing is published without the trainer's agreement and the
 * switch. Saved as it goes: the agreement and the switch at once, the texts
 * with their button, the photo when chosen.
 */
export default function PublicProfileDialog({ trainer, canMutate, onClose, onChanged }) {
  const [p, setP] = useState(() => profileOf(trainer));
  const [texts, setTexts] = useState(() => textsOf(profileOf(trainer)));
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const live = { ...trainer, public_profile: p };
  const dirty = Object.keys(texts).some((k) => (texts[k] || "") !== (p[k] || ""));
  const consented = Boolean(p.consent?.at);

  const put = async (body, what) => {
    setBusy(what);
    try {
      const r = await api.put(`/admin/trainers/${trainer.id}/public-profile`, body);
      setP(r.data);
      if ("display_name" in body) setTexts(textsOf(r.data));
      onChanged?.();
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva profilul."));
      return false;
    } finally {
      setBusy("");
    }
  };

  const upload = async (file) => {
    const problem = photoProblem(file);
    if (problem) { toast.error(problem); return; }
    const body = new FormData();
    body.append("file", file);
    setBusy("photo");
    try {
      const r = await api.post(`/admin/trainers/${trainer.id}/photo`, body);
      setP(r.data);
      toast.success("Poza e salvată.");
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut încărca poza."));
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    setBusy("photo");
    try {
      const r = await api.delete(`/admin/trainers/${trainer.id}/photo`);
      setP(r.data);
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut șterge poza."));
    } finally {
      setBusy("");
    }
  };

  const name = texts.display_name.trim() || trainer.name;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm" onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="public-profile-title"
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-[#E5E7EB] bg-white shadow-2xl"
        data-testid="trainer-public-profile">
        <div className="flex items-center justify-between border-b border-[#E5E7EB] p-5">
          <h3 id="public-profile-title" className="inline-flex items-center gap-2 font-display text-lg font-bold text-[#373A45]">
            <Globe size={18} /> Profil pe site · {trainer.name}
          </h3>
          <button type="button" onClick={onClose} disabled={Boolean(busy)} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <p className="text-sm text-[#6B7280]">
            Ce vede vizitatorul pe cardul grupelor lui, pe site și în checkout. Nimic nu apare fără acordul formatorului.
          </p>

          <section className="space-y-3 rounded-2xl border border-[#E5E7EB] p-4">
            <label className="flex items-start gap-2 text-sm text-[#373A45]">
              <input type="checkbox" checked={consented} disabled={!canMutate || Boolean(busy)}
                onChange={(e) => put({ consent: e.target.checked }, "consent")}
                className="mt-0.5 h-4 w-4 accent-[#965EA7]" data-testid="trainer-profile-consent" />
              <span>
                Formatorul a fost de acord să-i publicăm poza și descrierea pe site.
                {consented && <span className="mt-0.5 block text-xs text-[#6B7280]">{consentLine(p.consent)} Păstrează acordul lui scris la dosar.</span>}
              </span>
            </label>
            <label className={`flex items-center justify-between gap-3 text-sm ${consented ? "text-[#373A45]" : "text-[#9CA3AF]"}`}>
              <span>
                <span className="font-semibold">Afișează pe site</span>
                <span className="block text-xs text-[#6B7280]">
                  {isPublic(live) ? "Profilul apare acum pe cardurile grupelor lui."
                    : consented ? "Profilul nu apare încă pe site." : "Bifează întâi acordul formatorului."}
                </span>
              </span>
              <input type="checkbox" role="switch" checked={Boolean(p.enabled)} disabled={!canMutate || !consented || Boolean(busy)}
                onChange={(e) => put({ enabled: e.target.checked }, "enabled")}
                className="h-5 w-9 accent-[#965EA7]" data-testid="trainer-profile-enabled" />
            </label>
          </section>

          <section className="flex items-center gap-4">
            <SpeakerAvatar name={name} photoUrl={adminPhotoSrc(p.photo_id)} size={88} />
            <div className="min-w-0 space-y-2">
              <input ref={fileRef} type="file" accept={PHOTO_ACCEPT} className="hidden"
                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="trainer-profile-photo-input" />
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={!canMutate || Boolean(busy)} onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50"
                  data-testid="trainer-profile-photo-upload">
                  {busy === "photo" ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                  {p.photo_id ? "Schimbă poza" : "Încarcă o poză"}
                </button>
                {p.photo_id && (
                  <button type="button" disabled={!canMutate || Boolean(busy)} onClick={removePhoto}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-[#9b1f2c] hover:bg-[#E63946]/10 disabled:opacity-50"
                    data-testid="trainer-profile-photo-remove">
                    <Trash2 size={13} /> Șterge poza
                  </button>
                )}
              </div>
              <p className="text-xs text-[#6B7280]">
                JPG, PNG sau WebP, cel mult 8 MB. O decupăm pătrat, din mijloc, și ștergem datele camerei (inclusiv locația).
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <Text label="Numele afișat" hint={`Gol: „${trainer.name}”.`} max={LIMITS.display_name}
              value={texts.display_name} onChange={(v) => setTexts({ ...texts, display_name: v })} disabled={!canMutate}
              placeholder={trainer.name} testid="trainer-profile-name" />
            <Text label="Titlul scurt" hint="Un rând sub nume." max={LIMITS.title} value={texts.title}
              onChange={(v) => setTexts({ ...texts, title: v })} disabled={!canMutate}
              placeholder="Profesoară de informatică, 12 ani la clasă" testid="trainer-profile-title" />
            <Text label="Descrierea" hint="Apare la „Despre formator”." max={LIMITS.bio} value={texts.bio} rows={5}
              onChange={(v) => setTexts({ ...texts, bio: v })} disabled={!canMutate}
              placeholder="Cu cine lucrează, ce predă, ce o pasionează în AI pentru educație." testid="trainer-profile-bio" />
            {canMutate && (
              <button type="button" disabled={!dirty || Boolean(busy)} onClick={() => put(texts, "texts")}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50"
                data-testid="trainer-profile-save">
                {busy === "texts" && <Loader2 size={14} className="animate-spin" />} Salvează textele
              </button>
            )}
          </section>

          <section className="rounded-2xl bg-[#F5F6F6] p-4" data-testid="trainer-profile-preview">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">Previzualizare pe card</div>
            <div className="mt-3 flex items-center gap-3 rounded-xl bg-white p-3">
              <SpeakerAvatar name={name} photoUrl={adminPhotoSrc(p.photo_id)} size={36} />
              <div className="min-w-0 text-sm leading-snug">
                <div className="text-[#373A45]">Formator: <strong>{name}</strong></div>
                {texts.title.trim() && <div className="text-xs text-[#6B7280]">{texts.title.trim()}</div>}
              </div>
            </div>
            {texts.bio.trim() && (
              <p className="mt-2 whitespace-pre-line px-1 text-xs text-[#4B5160]">
                <span className="font-semibold text-[#373A45]">Despre formator: </span>{texts.bio.trim()}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function textsOf(p) {
  return { display_name: p.display_name || "", title: p.title || "", bio: p.bio || "" };
}

function Text({ label, hint, max, value, onChange, rows, disabled, placeholder, testid }) {
  const Tag = rows ? "textarea" : "input";
  const remaining = left(value, max);
  return (
    <label className="block">
      <span className="flex items-baseline justify-between text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">
        {label}
        <span className={`normal-case tracking-normal tabular-nums ${remaining < 0 ? "text-[#9b1f2c]" : ""}`}>{remaining}</span>
      </span>
      <Tag value={value} rows={rows} maxLength={max} disabled={disabled} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className={`mt-1 w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-sm ${rows ? "resize-y" : ""}`} />
      {hint && <span className="mt-0.5 block text-[11px] text-[#6B7280]">{hint}</span>}
    </label>
  );
}
